import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '@/styles/components/wallet-menu.module.less';
import AddressIcon from '@/components/AddressIcon';
import { sensitiveAddress } from '@/utils';
import { CaretDownOutlined, LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useAppDispatch, useCombineSelector, useWallet } from '@/services';
import _ from 'lodash';
import { Button, Dropdown, Space } from 'antd';
import { useAccountLoginType } from '@/services/account';
import LocalImage from '@/components/LocalImage';
import { SupportChains } from '@/config';
import Transactions from './Transactions';
import { TransactionModel, REDUCER_NAME } from '@/services/reducers/transactions';
import { link } from '@/services/reducers/wallet';
import { SetCurChainId } from '@/components/wallet/WalletProvider';

const WalletInfo = ({ toLink }: { toLink: () => void }): ReactElement => {
    const loginType = useAccountLoginType();
    return (
        <div className={styles['wallet-info']}>
            <ChainChosen />
            {loginType == 'Logged' ? <Logged /> : <></>}
            {loginType == 'Connected' ? <UnLogin /> : <></>}
            {loginType == 'UnConnected' ? <UnConnected toLink={toLink} /> : <></>}
        </div>
    );
};

const ChainChosen = (): ReactElement => {
    const dropdownMenus = useMemo(() => {
        const dm = [];
        SupportChains.forEach((o) =>
            dm.push({
                key: o.name,
                label: o.label,
                icon: <LocalImage name={o.icon} width={32} dir="/images/wallet" type="svg" />,
            })
        );
        dm.push({
            key: 'undefined',
            label: 'UnConnected',
            icon: <QuestionCircleOutlined style={{ fontSize: 32 }} />,
        });
        return dm;
    }, []);
    const { chain } = useWallet();
    const [selectedChain, setChain] = useState(dropdownMenus[dropdownMenus.length - 1]);
    const switchingChainRef = useRef(false);
    const changeChain = useCallback(({ key }: { key: any }) => {
        const ch = _.findLast(dropdownMenus, (o) => o.key == key);
        if (!ch) {
            return;
        }
        const curChain = SupportChains.filter((o) => o.name == ch.key)[0];
        if (!curChain) return;
        if (switchingChainRef.current || chain?.chainId === curChain.chainId) return;
        switchingChainRef.current = true;
        SetCurChainId(curChain.chainId).finally(() => {
            switchingChainRef.current = false;
        });
    }, [chain?.chainId, dropdownMenus]);
    useEffect(() => {
        if (!chain) {
            setChain(dropdownMenus[dropdownMenus.length - 1]);
        } else {
            setChain(dropdownMenus.filter((o) => o.key == chain.name)[0]);
        }
    }, [chain, dropdownMenus]);

    return (
        <span className={styles['chain-type']}>
            <Dropdown overlayClassName={styles['dropdown-wapper']} menu={{ items: dropdownMenus.filter((o) => o.key != 'undefined'), onClick: changeChain }}>
                <span>
                    <span>{selectedChain.icon}</span>
                    <Space>
                        <CaretDownOutlined style={{ color: '#999', fontSize: 14 }} className={styles['chain-arrow']} />
                    </Space>
                </span>
            </Dropdown>
        </span>
    );
};

const UnConnected = ({ toLink }: { toLink: () => void }): ReactElement => {
    return (
        <Button type="link" onClick={toLink}>
            Connect Wallet
        </Button>
    );
};

const UnLogin = (): ReactElement => {
    const dispatch = useAppDispatch();
    const { address, chainId, chain, type, loginPending, loginError } = useWallet();
    const retryLogin = useCallback(() => {
        if (!address || !chain) return;
        dispatch(
            link({
                chainId,
                chain,
                address,
                connected: true,
                logged: false,
                type,
            })
        );
    }, [address, chain, chainId, dispatch, type]);
    return (
        <Button type="link" loading={loginPending} disabled={!address || !chain || loginPending} onClick={retryLogin}>
            {loginPending ? 'Signing' : loginError ? 'Retry Login' : 'Sign to Login'}
        </Button>
    );
};

const Logged = (): ReactElement => {
    const [open, setOpen] = useState(false);
    const { address } = useWallet();
    const onClose = useCallback(() => setOpen(false), [open]);

    return (
        <>
            <Button type="link" className={styles['to-profile']} onClick={() => setOpen(true)}>
                <span className={styles['icon']}>
                    <AddressIcon address={address} />
                </span>
                <span className={styles['address']}>{sensitiveAddress(address)}</span>
                <TransactionCounter />
            </Button>
            <Transactions visible={open} onClose={onClose} />
        </>
    );
};

const TransactionCounter = (): ReactElement => {
    const couter = useCombineSelector<TransactionModel>(REDUCER_NAME, (state) => state.counter);
    if (couter == 0) {
        return <></>;
    }
    return (
        <span className={styles['loading-btn']}>
            <LoadingOutlined style={{ fontSize: 32 }} />
            <span className={styles['loading-num']}>{couter}</span>
        </span>
    );
};

export default WalletInfo;
